// Persistent application state (profile, settings, progress, plan) in localStorage, with change notifications.
// The account's part (profile, progress, totals, plan, score) is a copy of the server's (ADR 0013); device settings and
// tour history belong to this browser.
import { levelFromXp, seedFtp, DEFAULT_CULTURE, type PlayerState } from '@gruvetto/core';
import type { FtpState, MyClubRef, PlanAdjust, Profile, RatingResponse } from '@gruvetto/contracts';

const KEY = 'gv-state-v1';

/** Settings chosen on this device. Keymaps and the trainer curve are free-form records owned by their screens. */
export interface Settings {
  gfx: string; photos: string; cameraView?: string; mood: string; trackMood?: boolean; perfHud: boolean; tdiff: number; volume: number; ambience: boolean;
  /** Effects, riding-sounds and music volumes (0-1); unset until the player moves them (audio/engine.ts mixLevels). */
  sfxVolume?: number; ambVolume?: number; musicVolume?: number; erg: boolean; assist: boolean;
  rideMap: Record<string, string> | null; keyMap: Record<string, string> | null; touchControls: string; trainerCurve: string | null; wheelMm: number;
  lastRace: { mode: string; track: string; laps: number; bots: number; diff: string; groupWkg: number; items: string[]; elastic?: boolean; /** Official items the player unticked (core withoutItems). */ itemsOff?: string[] };
}
/** Program progress, with the adaptation record kept by the engine. */
export type PlanProgressState = NonNullable<PlayerState['plan']> & { adjust?: PlanAdjust };
export interface State {
  /** Signed in with the profile set up: tours may be offered. */
  onboarded: boolean;
  profile: Profile;
  settings: Settings;
  progress: PlayerState['progress'] & { seenLevel?: number };
  totals: PlayerState['totals'];
  plan: PlanProgressState | null;
  rating: RatingResponse | null;
  /** The server's FTP estimate (ADR 0027): `profile.ftp` is its value, here with how it was obtained and how it moved. */
  ftp: FtpState | null;
  /** The player's club (id, name, tag), pulled with the account: the tag rides next to the name in every race. */
  club: MyClubRef | null;
  tours: { done: string[]; offered: string[] };
}

const DEFAULTS: State = {
  onboarded: false,
  profile: { name: 'Toi', mass: 75, ftp: seedFtp(75), color: '#ffd400', look: { pattern: 'uni', frame: 'couleur', helmet: 'couleur' }, culture: { ...DEFAULT_CULTURE } },
  settings: { gfx: 'haut', photos: 'auto', cameraView: 'chase', mood: 'golden', perfHud: false, tdiff: 50, volume: 0.7, ambience: true, erg: true, assist: true, rideMap: null, keyMap: null, touchControls: 'auto', trainerCurve: null, wheelMm: 2105, lastRace: { mode: 'race', track: 'collines', laps: 2, bots: 7, diff: 'normal', groupWkg: 2.5, items: [] } },
  progress: { xp: 0, achievements: [], seenLevel: 1 },
  totals: { rides: 0, km: 0, hours: 0, elevation: 0, wins: 0, hits: 0, workouts: 0, tests: 0 },
  plan: null, // { id, start, done: { 'w-i': activityId } }
  rating: null, // Gruvetto score, see data/rating.ts
  ftp: null,
  club: null,
  // guided tours: finished ones, and screens whose tour was already offered (kept on this device only)
  tours: { done: [], offered: [] },
};

const merge = (d: unknown, s: unknown): unknown => {
  if (Array.isArray(d) || typeof d !== 'object' || d === null) return s === undefined ? d : s;
  const base = d as Record<string, unknown>, src = (s || {}) as Record<string, unknown>, o = { ...base };
  for (const k in src) o[k] = k in base ? merge(base[k], src[k]) : src[k];
  return o;
};

function load(): State {
  let saved: unknown = null;
  try { saved = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { /* storage unavailable */ }
  // a copy: without a saved state the merge would hand out DEFAULTS' own objects, and every change would alter them
  return merge(structuredClone(DEFAULTS), saved || {}) as State;
}

const listeners = new Set<(s: State) => void>();
export const state: State = load();

export function save() {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota or private mode */ }
  for (const fn of listeners) fn(state);
}
export function onChange(fn: (s: State) => void) { listeners.add(fn); return () => listeners.delete(fn); }

/** Updates part of the state, then saves. */
export function update(fn: (s: State) => void) { fn(state); save(); }

export const level = () => levelFromXp(state.progress.xp);
export const wkg = () => state.profile.ftp / state.profile.mass;

/** Sign-out: the account's data leaves this browser; device settings and tour history stay. */
export function resetAccountData() {
  const d = structuredClone(DEFAULTS);
  Object.assign(state, { onboarded: false, profile: d.profile, progress: d.progress, totals: d.totals, plan: d.plan, rating: d.rating, club: d.club });
  save();
}

